import { Box, Center, Flex, Skeleton, Tooltip, useToast } from "@chakra-ui/react";
import { useContext } from "react";
import { useDropzone } from "react-dropzone";
import { useIntl } from "react-intl";

import { User } from "@nens/rana/api-v1";
import { CameraIcon } from "@nens/ranalib";
import { RanaErrors } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";

import { useChangeProfileImage } from "../api/use-change-profile-image";
import { Profile } from "../profile";
import { useProfile } from "../use-profile";

export const PROFILE_IMAGE_EDIT_BOX_SIZE = 24;

export function ProfileImageEdit({ user }: { user: User }) {
  const intl = useIntl();
  const toast = useToast();

  const { userImage, isApiKeyUser } = useProfile(user);
  const seeThroughOpacity = userImage ? 0.6 : 0.9;

  const { mutate: changeProfileImage, isMutating, errors } = useChangeProfileImage();

  const authInfo = useContext(TenantContext);
  const isEditable = !authInfo.isPublic && authInfo.user?.id === user.id && !isApiKeyUser;

  const onDrop = ([newProfileImage]: File[]) => {
    const reader = new FileReader();
    reader.onabort = () => console.error("file reading was aborted");
    reader.onerror = () => console.error("file reading has failed");
    reader.onload = () => {
      changeProfileImage(
        { id: user.id, imageUpload: newProfileImage },
        {
          onSuccess: () => {
            toast({
              title: intl.formatMessage({
                id: "success",
                defaultMessage: "Success!",
              }),
              description: intl.formatMessage({
                id: "success.profile_image_changed",
                defaultMessage: "Successfully changed profile image.",
              }),
            });
          },
        }
      );
    };
    reader.readAsDataURL(newProfileImage);
  };

  const accept = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg"],
    "image/webp": [".webp"],
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
  });

  if (!user) return null;

  return (
    <Flex direction="column" alignItems="center">
      <Tooltip
        placement="top"
        label={
          userImage
            ? intl.formatMessage({
                id: "profile.change_profile_picture",
                defaultMessage: "Change profile picture",
              })
            : intl.formatMessage({
                id: "profile.add_profile_picture",
                defaultMessage: "Add profile picture",
              })
        }
        isDisabled={!isEditable}
      >
        <div {...getRootProps({ onClick: (e) => e.stopPropagation() })}>
          <input {...getInputProps()} />
          <Box
            onClick={() => {
              if (isEditable) open();
            }}
            role="group"
            position="relative"
          >
            <Skeleton isLoaded={!isMutating} rounded="full" boxSize={PROFILE_IMAGE_EDIT_BOX_SIZE}>
              <Profile noModal boxSize={PROFILE_IMAGE_EDIT_BOX_SIZE} user={user} />
            </Skeleton>
            {isEditable && (
              <Center
                cursor="pointer"
                bg="brand.glacier"
                rounded="full"
                position="absolute"
                h="full"
                w="full"
                top="0"
                left="0"
                alignItems="center"
                opacity={isDragActive && !isEditable ? seeThroughOpacity : 0}
                _groupHover={{ opacity: seeThroughOpacity }}
                transition="all 300ms"
              >
                <CameraIcon opacity={1} color="brand.analytic" boxSize={8} />
              </Center>
            )}
          </Box>
        </div>
      </Tooltip>
      <RanaErrors errors={errors} />
    </Flex>
  );
}
